import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PageHeader } from '@/components/ui';
import { connectionService } from '@/services/connectionService';

interface CashfreeFormData {
  appId: string;
  secretKey: string;
}

export const CashfreeConnectionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<CashfreeFormData>({
    appId: '',
    secretKey: '',
  });
  const [errors, setErrors] = useState<Partial<CashfreeFormData>>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (field: keyof CashfreeFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<CashfreeFormData> = {};
    
    if (!formData.appId.trim()) {
      newErrors.appId = 'App ID (x-client-id) is required';
    }
    
    if (!formData.secretKey.trim()) {
      newErrors.secretKey = 'Secret Key (x-secret-key) is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    // Get payment gateway UUID from location state first, then fallback to URL params
    const paymentGatewayUuid = (location.state as any)?.payment_gateway_uuid || 
      searchParams.get('payment_gateway');
    if (!paymentGatewayUuid) {
      toast.error('Payment gateway UUID is required', {
        style: { background: '#d32f2f', color: '#fff' },
      });
      return;
    }

    try {
      setLoading(true);
      const response = await connectionService.setupCashfree({
        payment_gateway: paymentGatewayUuid,
        payment_flow: 2,
        config: {
          api_key: formData.appId.trim(),
          api_secret: formData.secretKey.trim(),
        },
      });

      if (response.success) {
        toast.success(response.message || 'Cashfree connection setup successfully!', {
          duration: 4000,
          style: { background: '#43a047', color: '#fff' },
        });
        // Use setTimeout to ensure toast is visible before redirect
        setTimeout(() => {
          navigate('/merchant/connections', { replace: true });
        }, 500);
      } else {
        toast.error(response.message || 'Failed to setup Cashfree connection', {
          style: { background: '#d32f2f', color: '#fff' },
        });
      }
    } catch (error: any) {
      const errorMessage = error?.message || error?.response?.data?.message || 'An error occurred while setting up Cashfree connection';
      toast.error(errorMessage, {
        style: { background: '#d32f2f', color: '#fff' },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/merchant/connections');
  };

  const isFormValid = formData.appId.trim() && formData.secretKey.trim();

  return (
    <Box>
      <PageHeader
        title="Add Cashfree"
        showDivider={false}
      />

      <Paper sx={{ p: 4, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Set up credentials
        </Typography>

        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="App ID (x-client-id)"
            placeholder="Enter App ID (x-client-id)"
            value={formData.appId}
            onChange={(e) => handleChange('appId', e.target.value)}
            error={!!errors.appId}
            helperText={errors.appId}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="Secret Key (x-secret-key)"
            placeholder="Enter Secret Key (x-secret-key)"
            value={formData.secretKey}
            onChange={(e) => handleChange('secretKey', e.target.value)}
            error={!!errors.secretKey}
            helperText={errors.secretKey}
            type="password"
            required
          />
        </Box>
      </Paper>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            textTransform: 'none',
            borderColor: 'primary.main',
            color: 'primary.main',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isFormValid || loading}
          sx={{
            textTransform: 'none',
            bgcolor: !isFormValid || loading ? 'grey.300' : 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: !isFormValid || loading ? 'grey.300' : 'primary.dark',
            },
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CashfreeConnectionPage;

